import { isAxiosError } from "axios";
import axiosInstance from "../../../../common/axiosInstance";
import { sanitizeExportRow } from "../../../../common/utils";
import * as XLSX from "xlsx";

export interface HygieneScoreTrend {
  date: string;
  washroomId: string;
  washroomName: string;
  premisesName: string;
  regionName: string;
  hygieneScore: number;
  auditCount: number;
  maxScore: number;
  minScore: number;
}

export interface ComplianceReport {
  washroomId: string;
  washroomName: string;
  premisesName: string;
  regionName: string;
  floor: string;
  type: string;
  totalInspections: number;
  completedInspections: number;
  pendingInspections: number;
  complianceRate: number;
  avgScore: number;
  completedAvgScore: number;
  lastAuditDate: string | null;
}

export interface CombinedReport {
  summary: {
    totalWashrooms: number;
    totalTrendPoints: number;
    averageComplianceRate: number;
    averageHygieneScore: number;
    totalInspections: number;
    dateRange: {
      startDate: string | null;
      endDate: string | null;
    };
    appliedFilters: {
      region: string | null;
      premises: string | null;
      washroom: string | null;
    };
  };
  data: {
    hygieneScoreTrends: HygieneScoreTrend[];
    complianceReports: ComplianceReport[];
  };
}

export interface ReportFilters {
  region?: string;
  premises?: string;
  washroom?: string;
  start_date?: string;
  end_date?: string;
  frequency?: "Daily" | "Weekly" | "Monthly" | "Yearly";
}

export type DateRange = "daily" | "weekly" | "monthly";

// Utility function to get date ranges
export const getDateRange = (range: DateRange) => {
  const today = new Date();
  const formatDate = (date: Date) => date.toISOString().split("T")[0];

  switch (range) {
    case "daily":
      return {
        start_date: formatDate(today),
        end_date: formatDate(today),
      };
    case "weekly":
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);
      return {
        start_date: formatDate(weekAgo),
        end_date: formatDate(today),
      };
    case "monthly":
      const monthAgo = new Date(today);
      monthAgo.setMonth(today.getMonth() - 1);
      return {
        start_date: formatDate(monthAgo),
        end_date: formatDate(today),
      };
    default:
      return {};
  }
};

const handleApiError = (error: any): { message: string; status?: number } => {
  if (isAxiosError(error)) {
    if (error.response) {
      if (error.response.status === 401) {
        return {
          message: "Authentication failed. Please log in again.",
          status: 401,
        };
      }
      if (error.response.status === 403) {
        return {
          message: "You do not have permission to access this data.",
          status: 403,
        };
      }
      return {
        message:
          error.response.data?.error ||
          error.response.data?.message ||
          "An error occurred while communicating with the server.",
        status: error.response.status,
      };
    }
    return {
      message: "Network error. Please check your connection and try again.",
    };
  }
  return { message: "An unexpected error occurred." };
};

export const fetchHygieneScoreTrends = async (
  filters: ReportFilters = {}
): Promise<HygieneScoreTrend[]> => {
  try {
    const queryParams = new URLSearchParams();

    // Add filters to query parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "") {
        queryParams.append(key, value);
      }
    });

    const url = `/api/washroom-reports/hygiene-trends${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await axiosInstance.get(url);

    if (response.data.success) {
      return response.data.data || [];
    } else {
      throw new Error(
        response.data.message || "Failed to fetch hygiene score trends"
      );
    }
  } catch (error) {
    const errorInfo = handleApiError(error);
    throw new Error(errorInfo.message);
  }
};

export const fetchComplianceReports = async (
  filters: ReportFilters = {}
): Promise<{
  summary: any;
  data: ComplianceReport[];
}> => {
  try {
    const queryParams = new URLSearchParams();

    // Add filters to query parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "") {
        queryParams.append(key, value);
      }
    });

    const url = `/api/washroom-reports/compliance${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await axiosInstance.get(url);

    if (response.data.success) {
      return {
        summary: response.data.summary || {},
        data: response.data.data || [],
      };
    } else {
      throw new Error(
        response.data.message || "Failed to fetch compliance reports"
      );
    }
  } catch (error) {
    const errorInfo = handleApiError(error);
    throw new Error(errorInfo.message);
  }
};

export const fetchCombinedReport = async (
  filters: ReportFilters = {}
): Promise<CombinedReport> => {
  try {
    const queryParams = new URLSearchParams();

    // Add filters to query parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "") {
        queryParams.append(key, value);
      }
    });

    const url = `/api/washroom-reports/combined${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await axiosInstance.get(url);

    if (response.data.success) {
      return response.data;
    } else {
      throw new Error(
        response.data.message || "Failed to fetch combined report"
      );
    }
  } catch (error) {
    const errorInfo = handleApiError(error);
    throw new Error(errorInfo.message);
  }
};

// Excel export function
export const exportReportsToExcel = async (
  filters: ReportFilters = {}
): Promise<void> => {
  try {
    // Fetch the combined report data
    const reportData = await fetchCombinedReport(filters);

    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Create Summary sheet
    const summaryData = [
      ["Washroom Reports Summary", ""],
      ["", ""],
      ["Total Washrooms", reportData.summary.totalWashrooms || 0],
      ["Total Trend Points", reportData.summary.totalTrendPoints || 0],
      [
        "Average Compliance Rate (%)",
        reportData.summary.averageComplianceRate || 0,
      ],
      ["Average Hygiene Score", reportData.summary.averageHygieneScore || 0],
      ["Total Inspections", reportData.summary.totalInspections || 0],
      ["", ""],
      ["Applied Filters:", ""],
      ["Region", reportData.summary.appliedFilters?.region || "All"],
      ["Premises", reportData.summary.appliedFilters?.premises || "All"],
      ["Washroom", reportData.summary.appliedFilters?.washroom || "All"],
      ["Start Date", reportData.summary.dateRange?.startDate || "N/A"],
      ["End Date", reportData.summary.dateRange?.endDate || "N/A"],
      ["", ""],
      ["Report Generated", new Date().toLocaleString()],
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData.map(sanitizeExportRow));
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Create Hygiene Trends sheet
    if (
      reportData.data.hygieneScoreTrends &&
      reportData.data.hygieneScoreTrends.length > 0
    ) {
      const trendsHeaders = [
        "Date",
        "Washroom ID",
        "Washroom Name",
        "Premises Name",
        "Region Name",
        "Hygiene Score",
        "Audit Count",
        "Max Score",
        "Min Score",
      ];

      const trendsData = reportData.data.hygieneScoreTrends.map((trend) => [
        trend.date,
        trend.washroomId,
        trend.washroomName,
        trend.premisesName,
        trend.regionName || "N/A",
        trend.hygieneScore,
        trend.auditCount,
        trend.maxScore || "N/A",
        trend.minScore || "N/A",
      ]);

      const trendsSheet = XLSX.utils.aoa_to_sheet([
        trendsHeaders,
        ...trendsData.map(sanitizeExportRow),
      ]);
      XLSX.utils.book_append_sheet(workbook, trendsSheet, "Hygiene Trends");
    }

    // Create Compliance Reports sheet
    if (
      reportData.data.complianceReports &&
      reportData.data.complianceReports.length > 0
    ) {
      const complianceHeaders = [
        "Washroom ID",
        "Washroom Name",
        "Premises Name",
        "Region Name",
        "Floor",
        "Type",
        "Total Inspections",
        "Completed Inspections",
        "Pending Inspections",
        "Compliance Rate (%)",
        "Average Score",
        "Completed Average Score",
        "Last Audit Date",
      ];

      const complianceData = reportData.data.complianceReports.map((report) => [
        report.washroomId,
        report.washroomName,
        report.premisesName,
        report.regionName || "N/A",
        report.floor,
        report.type,
        report.totalInspections,
        report.completedInspections,
        report.pendingInspections,
        report.complianceRate,
        report.avgScore,
        report.completedAvgScore,
        report.lastAuditDate,
      ]);

      const complianceSheet = XLSX.utils.aoa_to_sheet([
        complianceHeaders,
        ...complianceData.map(sanitizeExportRow),
      ]);
      XLSX.utils.book_append_sheet(
        workbook,
        complianceSheet,
        "Compliance Reports"
      );
    }

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `Washroom_Reports_${timestamp}.xlsx`;

    // Write and download the file
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    const errorInfo = handleApiError(error);
    throw new Error(`Failed to export reports: ${errorInfo.message}`);
  }
};
